import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  isDevMode,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { IUserInformation } from '@interfaces/userInformation';
import { CurrentTheme } from '@models/uiapp-config';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { SecurityPolicies } from '@services/auth-variables';
import { AuthService } from '@services/auth.service';
import { UserService } from '@services/user.service';
import { EventEmiterService } from '@services/event.emmiter.service';
import { LogService } from '@services/log.service';
import { MapService } from '@services/map.service';
import { ProgressBarService } from '@services/progress-bar.service';
import { TankFilterService } from '@services/tank-filter.service';
import { TankProfileService } from '@services/tank-profile.service';
import { ThemeSettingsService } from '@services/theme-settings.service';
import { UtilsService } from '@services/utils.service';
import {
  selectLastSelectedTankProfile,
  selectLastSelectedTankProfileId,
} from '@state/selectors/tank-profile.selectors';
import { getActiveRoutes } from '@utils/utils';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  interval,
  Observable,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { AppState } from './app.state';
import { AppSettings } from './shared/Constants/sharedConstants';
import * as TankProfileActions from './state/actions/tank-profile.actions';
import * as UserProfileActions from './state/actions/user-profile.actions';
import { SettingsDialogService } from '@components/settings/services/settings-dialog.service';
import { selectUserInformation } from '@state/selectors/user-profile.selectors';
import { exit } from 'process';
import { AppDialogService } from '@services/app-dialog.service';

@Component({
  selector: 'app-auth-shell',
  templateUrl: './auth-shell.component.html',
  styleUrls: ['./auth-shell.component.scss'],
})
export class AuthShellComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('sideNavMenuElement') sideNavMenuElement: ElementRef;
  @ViewChild('sidenav') sidenav: MatSidenav;

  public policies: any = SecurityPolicies;

  legacyAppLoginURL: string;
  logoHyperlink: string;
  menuPanelIsClosed = false;
  menuPanelWidth: string;
  sideNavWidth: string;
  sideNavWidthNarrow: string;
  isDashboard: boolean = true;
  sideNavTop: string;
  expandIcon: string;
  collapseIcon: string;

  validateOnLeave = false;
  title = 'app';
  selectPlaceholder = 'Current Season';
  selectClass = 'select-component';

  isDevMode: boolean;

  hideTankFilters$ = new BehaviorSubject(false);
  hideTitleBarFilters$ = new BehaviorSubject(true);
  pageTitleBarResourceKey: string;
  pageList: { name: string; resource: string }[] = [
    { name: 'notifications', resource: 'Header.TankNotificationsPageTitle' },
    { name: 'organization', resource: 'Header.OrganizationsPageTitle' },
    { name: 'report', resource: 'Header.ReportsPageTitle' },
    { name: 'user', resource: 'Header.UserPageTitle' },
    { name: 'group', resource: 'Header.GroupPageTitle' },
    { name: 'location', resource: 'Header.Locations' },
    { name: 'my-account', resource: 'Header.MyAccount' },
  ];

  loading$ = this._pbs.loading$;

  hasInitialized = false;

  userLoaded$: Subject<boolean> = new Subject<boolean>();

  lastSelectedProfileId$: Observable<number>;
  currentTankProfile$: Observable<any>;

  logoSource: string;
  heartbeatLogoUrl: string;

  showBuildInfo: boolean = AppSettings.showBuildInfo;
  showBuildInfo$: Observable<boolean> = AppSettings.showBuildInfo$;
  buildInfo: string;
  loggedIn$ = this.authService.loggedIn$.asObservable();
  themeEnabled: boolean;

  get isImpersonating(): boolean {
    return this.authService.getIsImpersonating();
  }
  currentUser$ = this.store.select(selectUserInformation);
  /** For this page load only - the banner comes back on the next sign-in, as legacy did. */
  passwordBannerDismissed = false;

  private subs: Subscription[] = [];
  private _destroy$ = new Subject();

  constructor(
    private router: Router,
    private store: Store<AppState>,
    private tankProfileService: TankProfileService,
    private logger: LogService,
    private authService: AuthService,
    private eventEmiterService: EventEmiterService,
    private utilsService: UtilsService,
    private themeSettings: ThemeSettingsService,
    public translate: TranslateService,
    private tankFilterService: TankFilterService,
    private _cdr: ChangeDetectorRef,
    private _pbs: ProgressBarService,
    private _ms: MapService,
    private settingsDialogService: SettingsDialogService,
    private userService: UserService,
    private _ads: AppDialogService
  ) {
    // Used in order to refresh the current route when the season is changed.
    this.isDevMode = isDevMode();
    //setup app heartbeat to keep auth token alive
    const heartbeatInterval = interval(1000 * 60 * AppSettings.heartBeatInterval);
    this.subs.push(
      heartbeatInterval.subscribe(() => {
        console.info('Heartbeat interval triggered!');
        this.authService.heartbeat().subscribe({
          next: result => {
            console.info('Heartbeat successfull');

            // The heartbeat is the only thing that catches an organization change this app did not
            // make - the legacy app writing the shared row, or the mobile app's override header.
            // It reports and nothing more, so this behaves exactly like the SignalR push does.
            if (result?.organizationChanged) {
              this.authService.applyExternalOrganizationChange(
                result.organizationId,
                result.organizationName
              );
            }
          },
          error: err => {
            // A 401 here is the session expiring; AuthInterceptor acts on it.
            console.error('Heartbeat failed!', err);
          },
        });
      })
    );

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .pipe(takeUntil(this._destroy$))
      .subscribe((event: NavigationEnd) => {
        this.changeCollapseIcons(event);
        const eventUrl = event?.url || '';
        this.hideTankFilters$.next(!this.isTankFilteredURL(event, eventUrl));
        this.getTitleHeaderText(eventUrl);
        this._cdr.markForCheck();
      });
  }

  getTitleHeaderText(eventUrl) {
    this.hideTitleBarFilters$.next(true);
    this.pageList.some(page => {

    });

    this.pageList.forEach(page => {
      if (
        (page.name != 'group' || !eventUrl.includes('group-list')) &&
        eventUrl.includes(page.name)
      ) {
        this.pageTitleBarResourceKey = page.resource;
        this.hideTitleBarFilters$.next(false);
      }
    });
  }

  ngAfterViewInit() {
    this.sideNavWidth = this.utilsService.getSideNavWidth();
    this.sideNavWidthNarrow = this.utilsService.getSideNavWidthNarrow();
    this.sideNavTop = this.utilsService.getSideNavTop();
    this.menuPanelWidth = this.sideNavWidth;

    this.authService.subscribeToLogoutNotifications();
    this.authService.subscribeToOrganizationChangeNotifications();
  }

  ngOnInit() {
    this.initializeSiteTheming();
    this.logger.enableHeartBeatTimer(30);
    this.logger.enableLinkTracking(true);
    this.intializeApplication();
  }

  ngOnDestroy(): void {
    // _destroy$ feeds takeUntil on the router-events and theme subscriptions; without this
    // it never emits and neither of them is ever torn down.
    this._destroy$.next(null);
    this._destroy$.complete();

    if (this.subs.length > 0) {
      this.subs.forEach(sub => sub.unsubscribe());
    }

    if (this.hideTankFilters$.closed) this.hideTankFilters$.complete();

    if (this.hideTitleBarFilters$.closed) this.hideTitleBarFilters$.complete();
  }

  showLeftSideNavNarrow(): void {
    this.menuPanelIsClosed = true;
    this.resizeLeftSideNav(this.sideNavWidthNarrow);
  }

  showLeftSideNav(): void {
    this.menuPanelIsClosed = false;
    this.resizeLeftSideNav(this.sideNavWidth);
  }

  resizeLeftSideNav(width: string): void {
    this.sidenav.close();
    this.menuPanelWidth = width;
    setTimeout(() => {
      this.sidenav.open();
    }, 0);
  }

  getMenuClosePosition(): string {
    let width = Number(this.menuPanelWidth?.substring(0, this.menuPanelWidth?.length - 2));
    width -= 20;
    return width + 'px';
  }

  getNavRouterLink(pathSegment: string): any[] {
    let rtn = [pathSegment];
    this.logger.log('getNavRouterLink', rtn);
    return rtn;
  }

  stopImpersonation(): void {
    this.userService.stopImpersonation().pipe(take(1)).subscribe({
      next: () => this.authService.getUserInformation(true).pipe(take(1)).subscribe(() => window.location.reload()),
      error: (err) => {
        this.logger.error('Failed to stop impersonation', err);
        this.translate.get('Impersonation.StopImpersonationFailedMessage')
          .pipe(take(1))
          .subscribe((res: string) => {
          this._ads.showErrorMessage(res);
        });
      },
    });
  }

  changeCollapseIcons(event) {
    if (event.url == '/dashboard' || event.url == '/') {
      this.isDashboard = true;
      this.expandIcon = 'assets/svg/expand.svg';
      this.collapseIcon = 'assets/svg/hide.svg';
    } else {
      this.isDashboard = false;
      this.expandIcon = 'assets/svg/expand-light.svg';
      this.collapseIcon = 'assets/svg/hide-light.svg';
    }
  }

  showSettingsSidebar() {
    this.settingsDialogService.openOrShowDialog();
  }

  private isTankFilteredURL(_event: NavigationEnd, _eventUrl: string) {
    const activeRoutes = getActiveRoutes(this.router.routerState.root);
    return activeRoutes.some(route => route.snapshot.data?.showTankFilters);
  }

  private intializeApplication() {
    //test for existing user information.
    this.subs.push(
      this.authService
        .getUserInformation()
        .pipe(takeUntil(this.userLoaded$)) //Initializing application layer
        .pipe(filter(userInfo => userInfo !== null))
        .subscribe(userInfo => {
          this.currentTankProfile$ = this.store.select(selectLastSelectedTankProfile);

          //setup click handlers for menu triggers in child components
          this.eventEmiterService.emitter.subscribe(event => {
            if (event === this.eventEmiterService.events.onTankProfileSettingsClicked) {
              this.showSettingsSidebar();
            } else if (event === this.eventEmiterService.events.onCollapseSidebar) {
              this.showLeftSideNavNarrow();
            }
          });

          this.loadUser(userInfo);
          this.initializeGoogleMap(userInfo);

          // set legacy app url link
          this.legacyAppLoginURL = userInfo.legacyAppLoginURL;

          //current org and TP
          const orgId = userInfo.lastSelectedOrganizationId
            ? userInfo.lastSelectedOrganizationId
            : 0;

          const tpId = userInfo.lastSelectedTankProfileId ? userInfo.lastSelectedTankProfileId : 0;

          //setup web tracking information.
          this.initializeWebTracking(userInfo, orgId, tpId);

          //save user Info to the store
          this.store.dispatch(new UserProfileActions.GetUserInfo(userInfo));

          this.initializeBuildInfo();

          //set the current Tank Filter to Default
          this.tankFilterService.resetLastSelectedTankQuickFilter();

          //get the Tank Profiles for this user.
          this.initializeTankProfile(tpId);
        })
    );
  }

  private initializeTankProfile(tpId: number) {
    this.tankProfileService
      .getTankProfile(tpId)
      .pipe(take(1))
      .subscribe(result => {
        this.store.dispatch(new TankProfileActions.UpdateCurrentTankProfile(result));
        // Deferred a tick: flipping hasInitialized here creates the app shell (and its
        // loading$ | async progress bar) for the first time in the same change-detection
        // pass that concurrent HTTP calls (via LoadingInterceptor) are still updating
        // ProgressBarService's loading state, which trips NG0100. See angular.io/errors/NG0100.
        setTimeout(() => {
          this.hasInitialized = true;
          this._cdr.markForCheck();
        });
      });
  }

  // The endpoint 403s anyone who is not a SuperUser, so only SuperUsers ask for it. Calling it on
  // every sign-in made it the largest source of failed requests in prod.
  private initializeBuildInfo() {
    this.authService
      .passesPolicyOf(SecurityPolicies.RequireSuperUser)
      .pipe(
        take(1),
        filter(isSuperUser => isSuperUser),
        switchMap(() => this.authService.GetBuildInfo()),
        take(1),
      )
      .subscribe(apiResults => {
        AppSettings.buildInfo = apiResults.buildInfo;
        this.buildInfo = AppSettings.buildInfo;
      });
  }

  private initializeWebTracking(userInfo: IUserInformation, orgId: number, tpId: number) {
    // The numeric id, not the login name: it matches enduser.id in Application Insights, so the
    // two tools bucket the same person the same way, and neither holds the name itself.
    this.logger.setUserId(String(userInfo.userId));
    // Slot 3, clear of the Cso/TankProfile pair below and of tank-profile.effect.ts, which share
    // the low slots. Visit scope: the customer does not change between pages.
    if (userInfo.billableOrganizationId) {
      this.logger.setCustomVariable(3, 'BillableOrg', String(userInfo.billableOrganizationId), 'visit');
    }
    this.logger.setCustomVariable(0, 'Cso', orgId.toString(), 'page');
    this.logger.setCustomVariable(1, 'TankProfile', tpId.toString(), 'page');
  }

  private initializeGoogleMap(userInfo: IUserInformation) {
    if(google && google.maps) {
            this._ms.mapIsInitialized = true;

    // Same NG0100 concern as initializeTankProfile: defer so the shell reveal gets its own
    // change-detection cycle instead of racing the loading interceptor's in-flight requests.
    if (AppSettings.tapStateExisted)
      setTimeout(() => {
        this.hasInitialized = true;
        this._cdr.markForCheck();
      });
  }
  }

  private initializeSiteTheming() {
    this.themeSettings.currentTheme$
      .asObservable()
      .pipe(
        tap(x => {
          this.themeEnabled = false;
          this.themeSettings.themeEnabled$.next(false);
        }),
        takeUntil(this._destroy$),
        distinctUntilChanged(),
        filter(x => {
          return x && x.themeName != null && x.themeName != '' && x.themeName != undefined;
        })
      )
      .subscribe((theme: CurrentTheme) => {
        this.themeEnabled = theme?.themeId !== 0;
        this.themeSettings.themeEnabled$.next(this.themeEnabled);
        //this.themeName = this.themeSettings.adaptiveThemeValue;
        this.logoSource = theme.LogoUrlPath;
        this.heartbeatLogoUrl = theme.heartbeatLogoUrlPath;
        this.logoHyperlink = theme.customerLogoHyperlink;
        this._cdr.detectChanges();
      });

    // The palette, the logo's background colour and the font are already on :root - the server set
    // them when it served the page - so there is nothing to apply here.
    this.logoHyperlink = AppSettings?.UIAppConfig?.currentTheme?.customerLogoHyperlink;
  }

  private loadUser(userInfo: IUserInformation) {
    window['userInformation'] = {
      userName: userInfo.userName,
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      organizationName: userInfo.organizationName,
    };

    if (typeof (window as any).FS === 'function') {
      (window as any).FS('setIdentity', {
        uid: userInfo.userId,
        properties: {
          displayName: userInfo.firstName + ' ' + userInfo.lastName,
          uid: userInfo.userId,
          userName: userInfo.userName,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          email: userInfo.emailAddress,
          organizationName: userInfo.organizationName,
          lastSelectedOrganizationName: userInfo.lastSelectedOrganizationName,
          lastSelectedOrganizationId: userInfo.lastSelectedOrganizationId,
        },
      });
    }
    this.userLoaded$.next(true);
  }

}
